import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronRight, ChevronDown, GripVertical, Trash2, Plus, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import type { ProjectData } from './LineItemList';

interface ProjectHeaderProps {
  project: ProjectData;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  onAddLineItem: () => void;
  readOnly?: boolean;
  /** Actual member count from the flat array (may differ from project.lineItems.length during DnD) */
  memberCount?: number;
  /** Actual subtotal from the flat array members */
  memberSubtotal?: number;
  // Drag handle props from ProjectZone
  dragRef?: (node: HTMLElement | null) => void;
  dragStyle?: React.CSSProperties;
  dragHandleAttributes?: Record<string, any>;
  dragHandleListeners?: Record<string, any>;
  isDragging?: boolean;
  isChecked?: boolean;
  isIndeterminate?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export function ProjectHeader({
  project,
  isCollapsed,
  onToggleCollapse,
  onRename,
  onDelete,
  onAddLineItem,
  readOnly = false,
  memberCount,
  memberSubtotal,
  dragRef,
  dragStyle,
  dragHandleAttributes,
  dragHandleListeners,
  isDragging = false,
  isChecked,
  isIndeterminate,
  onCheckedChange,
}: ProjectHeaderProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(project.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate ?? false;
    }
  }, [isIndeterminate]);

  const handleSave = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== project.name) {
      onRename(trimmed);
    } else {
      setEditValue(project.name);
    }
    setIsEditing(false);
  }, [editValue, project.name, onRename]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(project.name);
      setIsEditing(false);
    }
  }, [handleSave, project.name]);

  const itemCount = memberCount ?? project.lineItems.length;
  const subtotal = memberSubtotal ?? project.lineItems.reduce((sum, item) => sum + item.totalCost, 0);
  const CollapseIcon = isCollapsed ? ChevronRight : ChevronDown;

  return (
    <div
      ref={dragRef}
      style={{ ...dragStyle, opacity: isDragging ? 0.5 : 1 }}
      className="flex items-center gap-2 px-3 py-2"
    >
      {onCheckedChange && (
        <input
          ref={checkboxRef}
          type="checkbox"
          checked={isChecked ?? false}
          onChange={(e) => {
            e.stopPropagation();
            onCheckedChange(e.target.checked);
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
        />
      )}
      {!readOnly && (
        <button
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          {...dragHandleAttributes}
          {...dragHandleListeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}

      <button
        onClick={onToggleCollapse}
        className="text-muted-foreground hover:text-foreground"
      >
        <CollapseIcon className="h-4 w-4" />
      </button>

      {isEditing ? (
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="h-7 text-sm font-semibold flex-1"
        />
      ) : (
        <div className="flex items-center gap-1 flex-1 min-w-0 group/name">
          <span className="text-sm font-semibold text-foreground truncate">
            {project.name}
          </span>
          {!readOnly && (
            <button
              onClick={() => {
                setEditValue(project.name);
                setIsEditing(true);
              }}
              className="text-muted-foreground/0 group-hover/name:text-muted-foreground hover:!text-foreground transition-colors flex-shrink-0"
              title="Rename project"
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {isCollapsed && (
        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
          {itemCount} item{itemCount !== 1 ? 's' : ''}
        </span>
      )}

      <span className="text-sm font-medium text-muted-foreground tabular-nums">
        ${subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>

      {!readOnly && (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            onClick={onAddLineItem}
            title="Add line item to project"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>

          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                title="Delete project"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete project &ldquo;{project.name}&rdquo;?</DialogTitle>
                <DialogDescription>
                  The project will be removed. Its {itemCount} line item{itemCount !== 1 ? 's' : ''} will become ungrouped.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <DialogClose asChild>
                  <Button variant="destructive" onClick={onDelete}>Delete</Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
